/**
 * StatsSection.tsx - Statistics Configuration Section
 *
 * This component provides an interface for configuring custom statistics
 * in the fundraiser builder details step with management and preview capabilities.
 *
 * Key Features:
 * - Statistics management interface
 * - Integration with StatsManager component
 * - Preview of configured statistics display
 * - Add, edit, and delete statistics
 * - Drag-and-drop reordering support
 * - Real-time validation and formatting
 *
 * Statistics Configuration:
 * - Label: Descriptive name for the statistic
 * - Value: Numerical or text value to display
 * - Display order management through drag-and-drop
 * - Format validation for consistency
 *
 * Management Features:
 * - Delegated to StatsManager component
 * - Consistent interface with other builder sections
 * - Real-time preview updates
 * - Validation and error handling
 * - Maximum statistics limit enforcement
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: StatsManager component, MobX store
 */

import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { StatsManager } from "../StatsManager";
import { CreateFundraiserModel } from "../../../models";
import { fundraiserStore } from "../../../stores";

const Section = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #e2e8f0;
`;

const HelpText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin-top: 16px;
  line-height: 1.4;
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 14px;
  font-weight: 500;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

interface StatsSectionProps {
  builderData: CreateFundraiserModel;
  shouldShowError: (fieldName: string) => boolean;
  onKeyDown?: (e: React.KeyboardEvent, isLastField: boolean) => void;
}

export const StatsSection: React.FC<StatsSectionProps> = observer(
  ({ builderData, shouldShowError, onKeyDown }) => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const hasError = fundraiserStore.builderData.errors.some((err) =>
      err.field?.startsWith("fundraiserStats")
    );

    // Auto-open section and scroll to it when there's a media error
    useEffect(() => {
      if (hasError && fundraiserStore.shouldScrollToStatsSection) {
        // Then scroll to it with a delay to allow the section to expand
        setTimeout(() => {
          if (sectionRef.current) {
            const elementTop = sectionRef.current.offsetTop;
            const headerOffset = 100;
            const targetPosition = elementTop - headerOffset;
            window.scrollTo({
              top: targetPosition,
              behavior: "smooth",
            });
          }
          fundraiserStore.setShouldScrollToStatsSection(false);
        }, 150); // Slightly longer delay to ensure section expansion
      } else {
      }
    }, [hasError, fundraiserStore.shouldScrollToStatsSection]);

    return (
      <Section ref={sectionRef} id="stats-section">
        <StatsManager onKeyDown={onKeyDown} />
        <HelpText>
          These stats will be displayed on your fundraiser page to show the
          impact of your campaign.
        </HelpText>
        {shouldShowError("stats") && (
          <ErrorMessage>
            {builderData.getErrorMessage("fundraiserStats")}
          </ErrorMessage>
        )}
      </Section>
    );
  }
);
